
import { ChargingStation } from '@/types/task';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Zap, MapPin, Home, Clock, CircleCheck, CircleX, Heart, Share2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import StarRating from './StarRating';
import { getPowerTypeLabel, formatPrice } from '@/utils/formatting';
import { cardHover, imageHover } from '@/components/ui/animations';
import { useToast } from '@/hooks/use-toast';
import { useState } from 'react';

interface StationCardProps {
  station: ChargingStation;
}

const StationCard = ({ station }: StationCardProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isFavorited, setIsFavorited] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  if (!station) {
    return null;
  }

  const handleViewDetails = () => {
    if (station.id) {
      navigate(`/station/${station.id}`);
      toast({
        title: "Loading station details",
        description: `Opening details for ${station.title}`,
      });
    }
  };

  const handleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsFavorited(!isFavorited);
    toast({
      title: isFavorited ? "Removed from favorites" : "Added to favorites",
      description: `${station.title} ${isFavorited ? 'removed from' : 'added to'} your favorites`,
    });
  };

  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      if (navigator.share) {
        await navigator.share({
          title: station.title,
          text: `Check out this charging station: ${station.title} at ${station.location}`,
          url: window.location.origin + `/station/${station.id}`,
        });
      } else {
        await navigator.clipboard.writeText(window.location.origin + `/station/${station.id}`);
        toast({
          title: "Link copied!",
          description: "Station link copied to clipboard",
        });
      }
    } catch (error) {
      toast({
        title: "Share failed",
        description: "Unable to share station link",
        variant: "destructive",
      });
    }
  };

  // Determine which image to show, prioritizing AI-generated images
  const displayImage = station.aiGeneratedImage || station.photos?.[0] || '/placeholder.svg';

  return (
    <Card className={`overflow-hidden ${cardHover} group cursor-pointer`} onClick={handleViewDetails}>
      <div className="relative h-48 overflow-hidden">
        {!imageLoaded && (
          <div className="absolute inset-0 bg-muted animate-pulse flex items-center justify-center">
            <Zap className="h-8 w-8 text-muted-foreground animate-spin" />
          </div>
        )}
        <img 
          src={displayImage} 
          alt={station.title || 'Charging Station'} 
          className={`object-cover w-full h-full transition-all duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'} ${imageHover}`}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageLoaded(true)}
        />
        
        {/* Action buttons overlay */}
        <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleFavorite}
            className="h-8 w-8 p-0 bg-background/80 backdrop-blur-sm hover:bg-background/90"
          >
            <Heart className={`h-3 w-3 ${isFavorited ? 'fill-red-500 text-red-500' : 'text-muted-foreground'}`} />
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleShare}
            className="h-8 w-8 p-0 bg-background/80 backdrop-blur-sm hover:bg-background/90"
          >
            <Share2 className="h-3 w-3 text-muted-foreground" />
          </Button>
        </div>
        {station.aiGeneratedImage && (
          <div className="absolute top-0 left-0 bg-blue-500 text-white px-2 py-1 text-xs m-3 rounded-md">
            AI Generated
          </div>
        )}
        {station.available ? (
          <div className="absolute top-0 right-0 bg-[#F2FCE2] text-green-700 px-3 py-1.5 text-xs m-3 rounded-full flex items-center gap-1.5 shadow-sm">
            <CircleCheck className="h-3.5 w-3.5" />
            <span>Available</span>
          </div>
        ) : (
          <div className="absolute top-0 right-0 bg-red-100 text-[#ea384c] px-3 py-1.5 text-xs m-3 rounded-full flex items-center gap-1.5 shadow-sm">
            <CircleX className="h-3.5 w-3.5" />
            <span>Unavailable</span>
          </div>
        )}
        {station.isHomeCharger && (
          <div className="absolute bottom-0 left-0 bg-blue-500 text-white px-3 py-1.5 text-xs m-3 rounded-full flex items-center gap-1.5 shadow-sm">
            <Home className="h-3.5 w-3.5" />
            <span>Home</span>
          </div>
        )}
      </div>
      
      <CardContent className="pt-5">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="font-semibold text-lg">{station.title || 'Unnamed Station'}</h3>
            <div className="flex items-center text-muted-foreground text-sm mt-1">
              <MapPin className="h-3 w-3 mr-1" />
              <span>{station.location || 'Unknown location'}</span>
            </div>
          </div>
          
          {station.rating && (
            <StarRating 
              rating={station.rating} 
              totalRatings={station.reviews?.length} 
              size={16}
            />
          )}
        </div>
        
        <div className="mt-3 flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-blue-500" />
            <span className="text-sm">
              {getPowerTypeLabel(station.chargerType || '', station.powerOutput || 0)} · {station.powerOutput || 0} kW
            </span>
          </div>
          
          {station.availableHours && (
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-muted-foreground">
                {station.availableHours}
              </span>
            </div>
          )}
          
          <div className="mt-2">
            <span className="text-lg font-bold">${formatPrice(station.price || 0)}</span>
            <span className="text-muted-foreground text-sm"> / kWh</span>
          </div>
        </div>
      </CardContent>
      
      <CardFooter className="pt-0">
        <div className="flex gap-2 w-full">
          <Button 
            onClick={handleViewDetails} 
            variant="default" 
            className="flex-1 transition-all duration-200 hover:shadow-md active:scale-95"
          >
            View Details
          </Button>
          {station.available && (
            <Button 
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/booking/${station.id}`);
                toast({
                  title: "Starting booking",
                  description: `Booking ${station.title}`,
                });
              }} 
              variant="outline" 
              className="transition-all duration-200 hover:shadow-md active:scale-95"
            >
              Book Now
            </Button>
          )}
        </div>
      </CardFooter>
    </Card>
  );
};

export default StationCard;
